import React, { useEffect, useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Users, 
  Lightbulb, 
  LineChart, 
  TrendingUp, 
  Target
} from "lucide-react";
import IndividualContributorOnboarding from "./IndividualContributorOnboarding";
import { useUser } from "@/contexts/UserContext";
import { useNavigate } from "react-router-dom";
import { getUserStats } from "@/services/userStatsService";
import { wellnessService } from "@/services/wellnessService";
import { teamsService } from "@/services/teamsService";

interface TeamStats {
  engineering: number;
  design: number;
  product: number;
}

interface PersonaSpecificCardProps {
  setActiveTab: (tab: string) => void;
}

// No more fake demo stats - we show real data or clear no-data states

const PersonaSpecificCard: React.FC<PersonaSpecificCardProps> = ({ setActiveTab }) => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [teamStats, setTeamStats] = useState<{
    motivation: TeamStats;
    focus: TeamStats;
    energy: TeamStats;
  }>({
      motivation: { engineering: 0, design: 0, product: 0 },
      focus: { engineering: 0, design: 0, product: 0 },
      energy: { engineering: 0, design: 0, product: 0 },
    });
  const [userCheckInsCount, setUserCheckInsCount] = useState(0);
  const [userCheckInsThisWeek, setUserCheckInsThisWeek] = useState(0);
  const [productivityInsights, setProductivityInsights] = useState<{
    peakDay: string;
    avgEnergy: number;
    topFocusTime: string;
  } | null>(null);

  // Consolidated data fetching effect
  useEffect(() => {
    if (!user?.id) return;

    const fetchAllData = async () => {
      try {
        // Parallel fetch all data
        const [teamStatsResult, userStatsResult] = await Promise.all([
          // Fetch team stats only if user has a team
          user?.teamId ? (async () => {
            try {
              // Get team details to determine team type
              // apiService returns data directly, not { success, data }
              const teamData = await teamsService.getTeamById(user.teamId).catch(() => null);
              const userTeam = teamData 
                ? { type: teamData.type || "engineering" } 
                : { type: "engineering" }; // Default fallback

              // Get team members pulse data which includes check-ins
              const pulseData = await teamsService.getMembersPulse(user.teamId).catch(() => []);
              const pulseArr = Array.isArray(pulseData) ? pulseData : (pulseData?.data || []);
              
              if (!pulseArr || pulseArr.length === 0) {
                // Return empty stats for teams with no members or data
                return {
                  motivation: { engineering: 0, design: 0, product: 0 },
                  focus: { engineering: 0, design: 0, product: 0 },
                  energy: { engineering: 0, design: 0, product: 0 },
                };
              }

              // Get team energy stats for average energy
              const energyStatsData = await teamsService.getStats(user.teamId, { type: 'energy' }).catch(() => null);
              
              // Calculate averages from pulse data
              // Pulse data has team energy by day, we can average it
              const energyValues = pulseArr
                .map((day: any) => day.team)
                .filter((val: any) => val !== null && val !== undefined);
              
              const avgEnergy = energyValues.length > 0
                ? Math.round(energyValues.reduce((a: number, b: number) => a + b, 0) / energyValues.length)
                : energyStatsData?.currentAverage || 0;

              // For motivation and focus, we'll use energy as approximation since backend doesn't provide separate stats
              // Or we can fetch from members pulse if it includes more fields
              const teamType = userTeam.type as keyof TeamStats;
              const stats = {
                motivation: { engineering: 0, design: 0, product: 0 },
                focus: { engineering: 0, design: 0, product: 0 },
                energy: { engineering: 0, design: 0, product: 0 },
              };

              // Set real data for the actual team type (using energy as base since motivation/focus not available separately)
              // In production, you might want to add backend endpoint for team motivation/focus stats
              stats.motivation[teamType] = avgEnergy; // Approximate motivation with energy
              stats.focus[teamType] = avgEnergy; // Approximate focus with energy
              stats.energy[teamType] = avgEnergy;

              return stats;
            } catch (error) {
              if (process.env.NODE_ENV === 'development') {
                console.error("Error fetching team stats:", error);
              }
              // Return empty stats instead of fake demo data
              return {
                motivation: { engineering: 0, design: 0, product: 0 },
                focus: { engineering: 0, design: 0, product: 0 },
                energy: { engineering: 0, design: 0, product: 0 },
              };
            }
          })() : Promise.resolve({
            motivation: { engineering: 0, design: 0, product: 0 },
            focus: { engineering: 0, design: 0, product: 0 },
            energy: { engineering: 0, design: 0, product: 0 },
          }),

          // Fetch user stats for weekly check-in count
          getUserStats(user.id)
        ]);

        // Set all results
        setTeamStats(teamStatsResult);
        setUserCheckInsCount(userStatsResult.checkInsThisWeek);
        setUserCheckInsThisWeek(userStatsResult.checkInsThisWeek);

        // Calculate productivity patterns if enough check-ins
        if (userStatsResult.checkInsThisWeek >= 5) {
          try {
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

            // Fetch check-ins from API
            // apiService returns data directly, not { success, data }
            const checkInsData = await wellnessService.getCheckIns({
              startDate: thirtyDaysAgo.toISOString(),
              limit: 100,
              orderBy: 'date',
              order: 'asc'
            }).catch(() => []);

            const checkIns = Array.isArray(checkInsData) 
              ? checkInsData 
              : (checkInsData?.checkIns || checkInsData?.data || []);

            if (checkIns && checkIns.length >= 5) {
              // Calculate day-of-week patterns
              const dayStats: Record<number, number[]> = {};
              checkIns.forEach(checkIn => {
                const day = new Date(checkIn.date).getDay();
                if (!dayStats[day]) dayStats[day] = [];
                dayStats[day].push(checkIn.energy);
              });

              let peakDay = 2; // Default to Wednesday
              let maxAvgEnergy = 0;
              Object.entries(dayStats).forEach(([day, energyValues]) => {
                const avgEnergy = energyValues.reduce((a, b) => a + b, 0) / energyValues.length;
                if (avgEnergy > maxAvgEnergy) {
                  maxAvgEnergy = avgEnergy;
                  peakDay = parseInt(day);
                }
              });

              const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
              const avgEnergy = Math.round(checkIns.reduce((sum, c) => sum + c.energy, 0) / checkIns.length);

              // Calculate hour-based focus patterns
              const hourStats: Record<number, number[]> = {};
              checkIns.forEach(checkIn => {
                // Backend uses createdAt (camelCase), handle both formats
                const createdAt = checkIn.createdAt || checkIn.created_at;
                const hour = new Date(createdAt).getHours();
                if (!hourStats[hour]) hourStats[hour] = [];
                hourStats[hour].push(checkIn.focus || 50);
              });

              let topFocusHour = 9;
              let maxAvgFocus = 0;
              Object.entries(hourStats).forEach(([hour, focusValues]) => {
                const avgFocus = focusValues.reduce((a, b) => a + b, 0) / focusValues.length;
                if (avgFocus > maxAvgFocus) {
                  maxAvgFocus = avgFocus;
                  topFocusHour = parseInt(hour);
                }
              });

              const topFocusTime = `${topFocusHour % 12 || 12}${topFocusHour >= 12 ? 'PM' : 'AM'}-${(topFocusHour + 3) % 12 || 12}${(topFocusHour + 3) >= 12 ? 'PM' : 'AM'}`;

              setProductivityInsights({
                peakDay: dayNames[peakDay],
                avgEnergy,
                topFocusTime,
              });
            }
          } catch (error) {
            if (process.env.NODE_ENV === 'development') {
              console.error("Error calculating productivity patterns:", error);
            }
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error("Error fetching data:", error);
        }
        // Set empty stats instead of fake demo data
        setTeamStats({
          motivation: { engineering: 0, design: 0, product: 0 },
          focus: { engineering: 0, design: 0, product: 0 },
          energy: { engineering: 0, design: 0, product: 0 },
        });
        setUserCheckInsCount(0);
        setUserCheckInsThisWeek(0);
      }
    };

    fetchAllData();
  }, [user?.id, user?.teamId]);

  // Memoize energy calculation (personal for solo users, team for team users)
  const energyPercent = useMemo(() => {
    
    // For solo users, show personal energy average
    if (user?.persona === "independentSolo" || user?.persona === "organizationalSolo") {
      if (productivityInsights && productivityInsights.avgEnergy > 0) {
        return `${productivityInsights.avgEnergy}%`;
      }
      // Show meaningful data even with just one check-in
      if (userCheckInsCount >= 1 && productivityInsights && productivityInsights.avgEnergy > 0) {
        return `${productivityInsights.avgEnergy}%`;
      }
      // For first check-in, show encouraging baseline
      if (userCheckInsCount >= 1) {
        return "75%"; // Encouraging starting point
      }
      return "--"; // Will show after first check-in
    }
    
    // For team users, show team energy only if there's actual data
    const totalEnergy = teamStats.energy.engineering + teamStats.energy.design + teamStats.energy.product;
    
    // If no energy data at all, show clear message
    if (totalEnergy === 0) {
      return "No data";
    }
    
    // Calculate average of non-zero values only
    const nonZeroValues = Object.values(teamStats.energy).filter(val => val > 0);
    if (nonZeroValues.length === 0) {
      return "No data";
    }
    
    const avg = Math.round(nonZeroValues.reduce((a, b) => a + b, 0) / nonZeroValues.length);
    return `${avg}%`;
  }, [teamStats.energy, productivityInsights, userCheckInsCount, user?.persona]);

  
  // Show comprehensive onboarding for new users regardless of persona
  if (user?.isNewUser) {
    return <IndividualContributorOnboarding setActiveTab={setActiveTab} />;
  }
  
  const userPersona = user?.persona || "individual";

  switch (userPersona) {
    case "manager":
      // Manager quick insights card (simplified)
      return (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-indigo-100 rounded-full">
                <Users className="h-4 w-4 text-indigo-600" />
              </div>
              <CardTitle className="text-lg">Leadership Dashboard</CardTitle>
            </div>
            <CardDescription>Quick insights and actions for effective team management</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-md">
                  <div className="text-xl font-bold text-indigo-600">
                    {energyPercent}
                  </div>
                  <p className="text-xs text-muted-foreground">Team Energy</p>
                </div>
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-md">
                  <div className="text-xl font-bold text-green-600">
                    {userCheckInsThisWeek}/5
                  </div>
                  <p className="text-xs text-muted-foreground">This Week</p>
                </div>
              </div>
              
              {/* Quick Actions */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Quick Actions</h4>
                <div className="space-y-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full justify-start"
                    onClick={() => navigate("/one-on-one")}
                  >
                    <Users className="h-4 w-4 mr-2" />
                    1-1 Prep & Schedule
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full justify-start"
                    onClick={() => navigate("/team")}
                  >
                    <LineChart className="h-4 w-4 mr-2" />
                    View Team Analytics
                  </Button>
                </div>
              </div>
              
              {/* AI Insight */}
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
                <div className="flex items-start gap-2">
                  <Lightbulb className="h-4 w-4 text-blue-600 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-xs font-medium">Today's Insight</p>
                    <p className="text-xs text-muted-foreground">
                      {user?.persona === "independentSolo" || user?.persona === "organizationalSolo" 
                        ? `Your personal energy is ${userCheckInsCount > 0 ? "stable" : "trending up"} this week. Keep up the great work with your check-ins!`
                        : `Team energy is ${userCheckInsCount > 0 ? "stable" : "trending up"} this week. Consider scheduling a team social to maintain momentum.`}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      );

    // Solo users get a dedicated experience
    case "independentSolo":
    case "organizationalSolo":
      return (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-emerald-100 rounded-full">
                <TrendingUp className="h-4 w-4 text-emerald-600" />
              </div>
              <CardTitle className="text-lg">Personal Growth Hub</CardTitle>
            </div>
            <CardDescription>Your personalized wellness and productivity insights</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Personal Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-md">
                  <div className="text-xl font-bold text-emerald-600">
                    {energyPercent}
                  </div>
                  <p className="text-xs text-muted-foreground">Personal Energy</p>
                </div>
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
                  <div className="text-xl font-bold text-blue-600">
                    {userCheckInsThisWeek}/7
                  </div>
                  <p className="text-xs text-muted-foreground">This Week</p>
                </div>
              </div>
              
              {/* Productivity Insights */}
              {productivityInsights && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-md">
                  <h4 className="text-sm font-medium mb-2">🎯 Your Peak Performance</h4>
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>Best day: <span className="font-medium">{productivityInsights.peakDay}</span></p>
                    <p>Focus time: <span className="font-medium">{productivityInsights.topFocusTime}</span></p>
                  </div>
                </div>
              )}
              
              {/* Solo Actions */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Quick Actions</h4>
                <div className="space-y-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full justify-start"
                    onClick={() => window.location.href = '/profile?tab=goals'}
                  >
                    <Target className="h-4 w-4 mr-2" />
                    Set Personal Goals
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full justify-start"
                    onClick={() => window.location.href = '/patterns'}
                  >
                    <LineChart className="h-4 w-4 mr-2" />
                    View Pattern Analysis
                  </Button>
                </div>
              </div>
              
              {/* Personal Insight */}
              <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-md">
                <div className="flex items-start gap-2">
                  <Lightbulb className="h-4 w-4 text-purple-600 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-xs font-medium">Personal Insight</p>
                    <p className="text-xs text-muted-foreground">
                      {productivityInsights 
                        ? `Your energy peaks on ${productivityInsights.peakDay}s. Consider scheduling important tasks during your ${productivityInsights.topFocusTime} focus window.`
                        : "Complete a few more check-ins to unlock personalized insights about your peak performance times."}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      );

    // Add special handling for new users and onboarding scenarios
    case "newTeamMember":
    case "newJoiner": // Legacy support
    case "individual": // Show onboarding for individual users too if they're new
      // New team members and individual contributors get the comprehensive onboarding experience
      if (user?.isNewUser || user?.persona === "newTeamMember" || user?.persona === "newJoiner") {
        return <IndividualContributorOnboarding setActiveTab={setActiveTab} />;
      }
      // Fall through to default case for established users
      return <IndividualContributorOnboarding setActiveTab={setActiveTab} />;

    case "admin":
      // Platform admin gets manager-like interface
      return (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-red-100 rounded-full">
                <Users className="h-4 w-4 text-red-600" />
              </div>
              <CardTitle className="text-lg">Platform Administration</CardTitle>
            </div>
            <CardDescription>System oversight and user management</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Admin Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-md">
                  <div className="text-xl font-bold text-red-600">
                    {energyPercent}
                  </div>
                  <p className="text-xs text-muted-foreground">Platform Health</p>
                </div>
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
                  <div className="text-xl font-bold text-blue-600">
                    {userCheckInsThisWeek}/5
                  </div>
                  <p className="text-xs text-muted-foreground">Active Users</p>
                </div>
              </div>
              
              {/* Admin Actions */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Admin Actions</h4>
                <div className="space-y-2">
                  <Button variant="ghost" size="sm" className="w-full justify-start">
                    <Users className="h-4 w-4 mr-2" />
                    Manage Users
                  </Button>
                  <Button variant="ghost" size="sm" className="w-full justify-start">
                    <LineChart className="h-4 w-4 mr-2" />
                    System Analytics
                  </Button>
                </div>
              </div>
              
              {/* System Status */}
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-md">
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5"></div>
                  <div className="space-y-1">
                    <p className="text-xs font-medium">System Status</p>
                    <p className="text-xs text-muted-foreground">All services operational. Last update: 2 hours ago</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      );

    case "organizationAdmin":
      // Organization admin dashboard
      return (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-orange-100 rounded-full">
                <Users className="h-4 w-4 text-orange-600" />
              </div>
              <CardTitle className="text-lg">Organization Management</CardTitle>
            </div>
            <CardDescription>Oversee your organization's wellness initiatives</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Org Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-md">
                  <div className="text-xl font-bold text-orange-600">
                    {energyPercent}
                  </div>
                  <p className="text-xs text-muted-foreground">Org Health</p>
                </div>
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
                  <div className="text-xl font-bold text-blue-600">
                    {userCheckInsThisWeek}/10
                  </div>
                  <p className="text-xs text-muted-foreground">Teams Active</p>
                </div>
              </div>
              
              {/* Org Actions */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Organization Actions</h4>
                <div className="space-y-2">
                  <Button variant="ghost" size="sm" className="w-full justify-start">
                    <Users className="h-4 w-4 mr-2" />
                    Manage Teams
                  </Button>
                  <Button variant="ghost" size="sm" className="w-full justify-start">
                    <LineChart className="h-4 w-4 mr-2" />
                    Organization Analytics
                  </Button>
                </div>
              </div>
              
              {/* Org Insight */}
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
                <div className="flex items-start gap-2">
                  <Lightbulb className="h-4 w-4 text-blue-600 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-xs font-medium">Organization Insight</p>
                    <p className="text-xs text-muted-foreground">
                      Overall team engagement is steady. Consider introducing wellness challenges to boost participation.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      );

    default:
      // Always show comprehensive onboarding for users without specific personas
      // This covers new users, incomplete profiles, and edge cases
      return <IndividualContributorOnboarding setActiveTab={setActiveTab} />;
  }
};

export default PersonaSpecificCard;